import React from 'react';
import { formatDayMonth, formatDateMedium, formatMonthYear } from '../../../../../utils/formatters';
import SearchSelect from '../../../../../components/SearchSelect';

interface FilterOption {
  value: string;
  label: string;
}

interface CalendarFilters {
  search: string;
  status: string;
  territory_id: string;
  assigned_to: string;
  context: 'all' | 'pos' | 'cmb';
  show_blocks: boolean;
  show_reminders: boolean;
}

interface CalendarFilterBarProps {
  calendarFilters: CalendarFilters;
  setCalendarFilters: React.Dispatch<React.SetStateAction<CalendarFilters>>;
  calendarFiltersExpanded: boolean;
  setCalendarFiltersExpanded: (expanded: boolean) => void;
  activeCalendarFilterCount: number;
  statusOptions: FilterOption[];
  territoryOptions: FilterOption[];
  operatorOptions: FilterOption[];
  filteredCalendarBookings: unknown[];
  showBlocks: boolean;
  viewMode: 'day' | 'week' | 'month' | 'list';
  isMobile: boolean;
  handlePrevDay: () => void;
  handlePrevWeek: () => void;
  handlePrevMonth: () => void;
  handleNextDay: () => void;
  handleNextWeek: () => void;
  handleNextMonth: () => void;
  handleToday: () => void;
  selectedDate: Date;
  currentMonth: Date;
}

/**
 * Shared Calendar Filter Bar - Uses DataTable's CSS classes for consistency
 * Same pattern as DataTable: .list-toolbar, .list-filters-inline, etc.
 */
export default function CalendarFilterBar({
  calendarFilters,
  setCalendarFilters,
  calendarFiltersExpanded,
  setCalendarFiltersExpanded,
  activeCalendarFilterCount,
  statusOptions,
  territoryOptions,
  operatorOptions,
  filteredCalendarBookings,
  showBlocks,
  viewMode,
  isMobile,
  // Navigation handlers
  handlePrevDay,
  handlePrevWeek,
  handlePrevMonth,
  handleNextDay,
  handleNextWeek,
  handleNextMonth,
  handleToday,
  selectedDate,
  currentMonth
}: CalendarFilterBarProps) {
  const ctx = calendarFilters.context;
  const ctxBtn = (target: 'all' | 'pos' | 'cmb', activeMod: string) =>
    `rounded-sm cursor-pointer text-xs toggle-btn${ctx === target ? ` ${activeMod}` : ''}`;

  return (
    <>
      {/* Toolbar - matches DataTable pattern */}
      <div className="list-toolbar">
        <div className="list-toolbar-left">
          {/* Context filter \u2014 All / POS / CMB. Uses shared toggle-btn pattern
              with brand-aware active modifiers from global.css. */}
          <div role="group" aria-label="Filter by customer type" className="flex gap-xs items-center">
            <button
              type="button"
              className={ctxBtn('all', 'toggle-btn--neutral-active')}
              onClick={() => setCalendarFilters(prev => ({ ...prev, context: 'all' }))}
            >
              All
            </button>
            <button
              type="button"
              className={ctxBtn('pos', 'toggle-btn--pos-active')}
              onClick={() => setCalendarFilters(prev => ({ ...prev, context: 'pos' }))}
            >
              POS
            </button>
            <button
              type="button"
              className={ctxBtn('cmb', 'toggle-btn--active')}
              onClick={() => setCalendarFilters(prev => ({ ...prev, context: 'cmb' }))}
            >
              CMB
            </button>
          </div>

          {/* Search */}
          <div className="list-search">
            <span className="list-search-icon">{'\uD83D\uDD0D'}</span>
            <input
              type="text"
              placeholder="Search bookings..."
              value={calendarFilters.search}
              onChange={(e) => setCalendarFilters(prev => ({ ...prev, search: e.target.value }))}
            />
            {calendarFilters.search && (
              <button
                className="list-search-clear"
                onClick={() => setCalendarFilters(prev => ({ ...prev, search: '' }))}
              >
                {'\u2715'}
              </button>
            )}
          </div>

          {/* Mobile: Filter toggle button */}
          <button
            className="list-filter-toggle"
            onClick={() => setCalendarFiltersExpanded(!calendarFiltersExpanded)}
          >
            <span>{'\u2699'}</span>
            <span>Filters</span>
            {activeCalendarFilterCount > 0 && (
              <span className="list-filter-badge">{activeCalendarFilterCount}</span>
            )}
            <span className="list-filter-arrow">{calendarFiltersExpanded ? '\u25B2' : '\u25BC'}</span>
          </button>

          {/* Desktop: Inline filters */}
          <div className="list-filters-inline">
            <button
              type="button"
              className={`rounded-sm cursor-pointer text-xs toggle-btn${calendarFilters.show_blocks !== false ? ' toggle-btn--active' : ''}`}
              onClick={() => setCalendarFilters(prev => ({ ...prev, show_blocks: prev.show_blocks === false }))}
              aria-pressed={calendarFilters.show_blocks !== false}
              title="Show or hide operator blocked time"
            >
              {calendarFilters.show_blocks !== false ? 'Blocks: on' : 'Blocks: off'}
            </button>
            <button
              type="button"
              className={`rounded-sm cursor-pointer text-xs toggle-btn${calendarFilters.show_reminders !== false ? ' toggle-btn--active' : ''}`}
              onClick={() => setCalendarFilters(prev => ({ ...prev, show_reminders: prev.show_reminders === false }))}
              aria-pressed={calendarFilters.show_reminders !== false}
              title="Show or hide follow-up reminders"
            >
              {calendarFilters.show_reminders !== false ? 'Reminders: on' : 'Reminders: off'}
            </button>

            <SearchSelect
              width="inline"
              options={[
                { value: 'all', label: 'All Statuses' },
                ...statusOptions.map(opt => ({ value: opt.value, label: opt.label })),
              ]}
              value={calendarFilters.status}
              onChange={(v) => setCalendarFilters(prev => ({ ...prev, status: v }))}
            />

            <SearchSelect
              width="inline"
              options={[
                { value: 'all', label: 'Territory: All' },
                ...territoryOptions.map(opt => ({ value: opt.value, label: opt.label })),
              ]}
              value={calendarFilters.territory_id}
              onChange={(v) => setCalendarFilters(prev => ({ ...prev, territory_id: v }))}
            />

            <SearchSelect
              width="inline"
              options={[
                { value: 'all', label: 'Operator: All' },
                ...operatorOptions.map(opt => ({ value: opt.value, label: opt.label })),
              ]}
              value={calendarFilters.assigned_to}
              onChange={(v) => setCalendarFilters(prev => ({ ...prev, assigned_to: v }))}
            />
          </div>
        </div>

        {/* Right side: Date navigation - adapts to view mode */}
        <div className="list-toolbar-right cal-nav-group">
          {/* Only show for calendar views */}
          {['day', 'week', 'month'].includes(viewMode) && (
            <>
              <button
                className="btn btn-sm btn-outline cal-nav-btn"
                onClick={viewMode === 'day' ? handlePrevDay : viewMode === 'week' ? handlePrevWeek : handlePrevMonth}
              >
                {'\u2039'}
              </button>
              <span className={`cal-date-label ${isMobile ? 'cal-date-label--mobile' : ''}`}>
                {viewMode === 'day' && (isMobile ? formatDayMonth(selectedDate) : formatDateMedium(selectedDate))}
                {viewMode === 'week' && (() => {
                  const startOfWeek = new Date(selectedDate);
                  startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay() + 1);
                  const endOfWeek = new Date(startOfWeek);
                  endOfWeek.setDate(startOfWeek.getDate() + 6);
                  return `${formatDayMonth(startOfWeek)} - ${formatDayMonth(endOfWeek)}`;
                })()}
                {viewMode === 'month' && formatMonthYear(currentMonth, !isMobile)}
              </span>
              <button
                className="btn btn-sm btn-outline cal-nav-btn"
                onClick={viewMode === 'day' ? handleNextDay : viewMode === 'week' ? handleNextWeek : handleNextMonth}
              >
                {'\u203A'}
              </button>
              <button
                className="btn btn-sm btn-primary cal-today-btn"
                onClick={handleToday}
              >
                Today
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile: Expandable filters */}
      <div className={`list-filters-mobile ${calendarFiltersExpanded ? 'expanded' : ''}`}>
        <button
          type="button"
          className={`rounded-sm cursor-pointer text-xs toggle-btn${calendarFilters.show_blocks !== false ? ' toggle-btn--active' : ''}`}
          onClick={() => setCalendarFilters(prev => ({ ...prev, show_blocks: prev.show_blocks === false }))}
          aria-pressed={calendarFilters.show_blocks !== false}
          title="Show or hide operator blocked time"
        >
          {calendarFilters.show_blocks !== false ? 'Blocks: on' : 'Blocks: off'}
        </button>
        <button
          type="button"
          className={`rounded-sm cursor-pointer text-xs toggle-btn${calendarFilters.show_reminders !== false ? ' toggle-btn--active' : ''}`}
          onClick={() => setCalendarFilters(prev => ({ ...prev, show_reminders: prev.show_reminders === false }))}
          aria-pressed={calendarFilters.show_reminders !== false}
          title="Show or hide follow-up reminders"
        >
          {calendarFilters.show_reminders !== false ? 'Reminders: on' : 'Reminders: off'}
        </button>

        <SearchSelect
          width="inline"
          options={[
            { value: 'all', label: 'All Statuses' },
            ...statusOptions.map(opt => ({ value: opt.value, label: opt.label })),
          ]}
          value={calendarFilters.status}
          onChange={(v) => setCalendarFilters(prev => ({ ...prev, status: v }))}
        />

        <SearchSelect
          width="inline"
          options={[
            { value: 'all', label: 'Territory: All' },
            ...territoryOptions.map(opt => ({ value: opt.value, label: opt.label })),
          ]}
          value={calendarFilters.territory_id}
          onChange={(v) => setCalendarFilters(prev => ({ ...prev, territory_id: v }))}
        />

        <SearchSelect
          width="inline"
          options={[
            { value: 'all', label: 'Operator: All' },
            ...operatorOptions.map(opt => ({ value: opt.value, label: opt.label })),
          ]}
          value={calendarFilters.assigned_to}
          onChange={(v) => setCalendarFilters(prev => ({ ...prev, assigned_to: v }))}
        />
      </div>

      {/* Results bar */}
      <div className="results-bar">
        {filteredCalendarBookings.length} bookings
        {showBlocks && ` \u2022 Showing ${calendarFilters.assigned_to !== 'all' ? 'operator' : ''} availability`}
      </div>
    </>
  );
}
